import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RotateCw, Pause, Maximize2, Crosshair, Eye, X } from "lucide-react";
import api from "../lib/api";

/**
 * Three.js based 3D plant viewer.
 *
 * Solves three pain points the previous <model-viewer> implementation had:
 *   1. PIPELINES: backend now ships pipes as visible-radius geometry; this
 *      viewer renders them with proper lighting so they read as real piping.
 *   2. RIGHT-CLICK PROPERTIES: contextmenu raycasts against named glTF nodes
 *      (`equipment::`, `pipe::`, `instrument::`, …) and opens a context menu;
 *      "Show properties" surfaces the metadata fetched from /model/meta.
 *   3. NAVIGATION: real OrbitControls with damping, right-button pan,
 *      zoom-to-cursor, framing button, configurable speed.
 */
export default function ModelViewer3D({ src, focusTag, projectId, runId }) {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [autoRotate, setAutoRotate] = useState(false);
  const [hoverLabel, setHoverLabel] = useState(null);
  const [menu, setMenu] = useState(null); // {x,y,nodeKey,nodeLabel}
  const [propsPanel, setPropsPanel] = useState(null); // {nodeKey, meta}
  const [meta, setMeta] = useState({}); // {nodeKey: {...}}
  const [loadState, setLoadState] = useState(src ? "loading" : "empty");

  // Fetch per-node metadata so the right-click menu has rich properties to show
  useEffect(() => {
    if (!projectId || !runId) return;
    let cancelled = false;
    api
      .get(`/projects/${projectId}/runs/${runId}/model/meta`)
      .then((res) => { if (!cancelled) setMeta(res.data?.nodes || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, runId]);

  // ---- Scene bootstrap ----
  useEffect(() => {
    if (!src || !mountRef.current) return;
    const mount = mountRef.current;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#0A0D14");

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 5000);
    camera.position.set(60, -60, 45);
    camera.up.set(0, 0, 1); // engineering Z-up

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    mount.appendChild(renderer.domElement);

    // Lights — hemisphere for soft fill + key sun + rim
    const hemi = new THREE.HemisphereLight(0xb8d0e8, 0x101418, 0.65);
    scene.add(hemi);
    const sun = new THREE.DirectionalLight(0xffffff, 1.15);
    sun.position.set(40, 60, 80);
    scene.add(sun);
    const rim = new THREE.DirectionalLight(0x9bb6d4, 0.45);
    rim.position.set(-50, -40, 40);
    scene.add(rim);

    // Grid floor for spatial reference (engineering blueprint feel)
    const grid = new THREE.GridHelper(120, 60, 0x1e4e8c, 0x152033);
    grid.rotation.x = Math.PI / 2; // turn grid to XY plane (Z-up)
    grid.position.z = -0.001;
    grid.material.opacity = 0.45;
    grid.material.transparent = true;
    scene.add(grid);

    // Controls — proper damping + right-button pan + zoom-to-cursor
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.85;
    controls.panSpeed = 1.2;
    controls.zoomSpeed = 1.0;
    controls.zoomToCursor = true;
    controls.screenSpacePanning = true;
    controls.minDistance = 1.5;
    controls.maxDistance = 600;
    controls.maxPolarAngle = Math.PI * 0.96; // a tiny bit past horizontal so user can look slightly up from below
    // Mouse mapping: LEFT=orbit, MIDDLE=zoom, RIGHT=pan (the natural CAD convention)
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };
    // Touch: 1=rotate, 2=pan+dolly
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };

    // Save handles
    stateRef.current = { scene, camera, renderer, controls, root: null, raycaster: new THREE.Raycaster(), pointer: new THREE.Vector2(), hoveredObject: null, hoveredOriginalEmissive: null };

    // Resize observer
    const resize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(h, 1);
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    // Animation loop
    let raf = 0;
    const tick = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // ---- Load glTF ----
    const loader = new GLTFLoader();
    setLoadState("loading");
    loader.load(
      src,
      (gltf) => {
        const root = gltf.scene;
        scene.add(root);
        stateRef.current.root = root;

        // Frame the scene
        frameAll(stateRef.current);
        setLoadState("ready");
      },
      undefined,
      (err) => {
        console.error("glTF load failed", err);
        setLoadState("error");
      },
    );

    // ---- Pointer + raycast events ----
    const ndcFromEvent = (ev) => {
      const rect = renderer.domElement.getBoundingClientRect();
      stateRef.current.pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      stateRef.current.pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    };
    const findNamedNode = (object) => {
      let cur = object;
      while (cur) {
        if (cur.userData && cur.userData.__nodeKey) return cur;
        if (cur.name && /^(equipment|pipe|instrument|support|steel|foundation|tray|duct|building|fire|clash)__/.test(cur.name)) {
          cur.userData.__nodeKey = cur.name;
          return cur;
        }
        cur = cur.parent;
      }
      return null;
    };
    const pickAt = (ev) => {
      if (!stateRef.current.root) return null;
      ndcFromEvent(ev);
      stateRef.current.raycaster.setFromCamera(stateRef.current.pointer, camera);
      const hits = stateRef.current.raycaster.intersectObjects([stateRef.current.root], true);
      for (const h of hits) {
        const n = findNamedNode(h.object);
        if (n) return n;
      }
      return null;
    };

    const onMove = (ev) => {
      const node = pickAt(ev);
      if (node) {
        if (stateRef.current.hoveredObject !== node) {
          // restore previous
          restoreHover(stateRef.current);
          // highlight new
          applyHover(stateRef.current, node);
        }
        setHoverLabel(node.userData.__nodeKey);
        renderer.domElement.style.cursor = "pointer";
      } else {
        if (stateRef.current.hoveredObject) restoreHover(stateRef.current);
        setHoverLabel(null);
        renderer.domElement.style.cursor = "grab";
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      const node = pickAt(ev);
      if (!node) {
        setMenu(null);
        return;
      }
      const rect = renderer.domElement.getBoundingClientRect();
      setMenu({
        x: ev.clientX - rect.left,
        y: ev.clientY - rect.top,
        nodeKey: node.userData.__nodeKey,
      });
    };
    const onClick = () => setMenu(null);

    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("contextmenu", onContextMenu);
    renderer.domElement.addEventListener("click", onClick);
    renderer.domElement.style.cursor = "grab";

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("contextmenu", onContextMenu);
      renderer.domElement.removeEventListener("click", onClick);
      controls.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      renderer.dispose();
      try { mount.removeChild(renderer.domElement); } catch {}
    };
  }, [src]);

  // Apply autoRotate
  useEffect(() => {
    const c = stateRef.current.controls;
    if (!c) return;
    c.autoRotate = autoRotate;
    c.autoRotateSpeed = 0.8;
  }, [autoRotate]);

  // Camera focus when an external focus tag is requested
  useEffect(() => {
    if (!focusTag || !stateRef.current.controls || !stateRef.current.camera) return;
    const { x, y, z } = focusTag;
    flyTo(stateRef.current, new THREE.Vector3(x, y, z), 16);
  }, [focusTag?.x, focusTag?.y, focusTag?.z, focusTag?.tag]); // eslint-disable-line

  const onFrameAll = () => frameAll(stateRef.current);
  const onResetView = () => {
    const { camera, controls } = stateRef.current;
    if (!camera || !controls) return;
    camera.position.set(60, -60, 45);
    controls.target.set(0, 0, 0);
    controls.update();
    frameAll(stateRef.current);
  };

  const activeNode = menu?.nodeKey ? meta[menu.nodeKey] || { kind: "unknown", label: menu.nodeKey } : null;

  // ---- Empty / loading states ----
  if (!src) {
    return (
      <div className="pf-blueprint-bg" style={{ height: "calc(100vh - 220px)", minHeight: 480, maxHeight: "82vh", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--pf-border)", padding: 40, textAlign: "center", color: "var(--pf-text-muted)" }}>
        <div>
          <div className="pf-overline" style={{ marginBottom: 10 }}>3D MODEL · EMPTY</div>
          <div>Run the pipeline — the 3D plant layout appears here as a glTF scene.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "calc(100vh - 220px)", minHeight: 480, maxHeight: "82vh", border: "1px solid var(--pf-border)", background: "#0A0D14", position: "relative", overflow: "hidden" }} data-testid="model-viewer-3d">
      <div ref={mountRef} style={{ position: "absolute", inset: 0 }} />

      {/* Loading shimmer */}
      {loadState === "loading" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--pf-text-muted)", fontFamily: "JetBrains Mono", fontSize: 12, letterSpacing: 1.2 }}>
          <span className="pf-spin" style={{ marginRight: 10 }}/> LOADING MODEL…
        </div>
      )}
      {loadState === "error" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#E2A0A0", fontFamily: "JetBrains Mono", fontSize: 12 }}>
          MODEL LOAD FAILED
        </div>
      )}

      {/* Top-right toolbar */}
      <div style={{ position: "absolute", top: 10, right: 10, zIndex: 3, display: "flex", gap: 6 }}>
        <button onClick={onFrameAll} className="btn btn-ghost" style={overlayBtnStyle()} title="Frame all (F)" data-testid="model-frame-all">
          <Maximize2 size={13}/> Fit
        </button>
        <button onClick={onResetView} className="btn btn-ghost" style={overlayBtnStyle()} title="Reset view" data-testid="model-reset-view">
          <Crosshair size={13}/> Reset
        </button>
        <button onClick={() => setAutoRotate((v) => !v)} className="btn btn-ghost" style={{ ...overlayBtnStyle(), borderColor: autoRotate ? "var(--pf-accent)" : "var(--pf-border)", color: autoRotate ? "var(--pf-accent)" : "var(--pf-text)" }} data-testid="model-auto-rotate-toggle">
          {autoRotate ? <><Pause size={13}/> Stop</> : <><RotateCw size={13}/> Rotate</>}
        </button>
      </div>

      {/* Hover hint */}
      {hoverLabel && !menu && (
        <div className="pf-mono" style={{ position: "absolute", left: 10, top: 10, background: "rgba(18,18,18,0.92)", border: "1px solid var(--pf-accent)", color: "var(--pf-accent)", padding: "5px 10px", fontSize: 11, letterSpacing: 1 }} data-testid="model-hover-label">
          {prettifyKey(hoverLabel)}
        </div>
      )}

      {/* Focus indicator (legacy: external locate-in-3D) */}
      {focusTag && !hoverLabel && !menu && (
        <div className="pf-mono" style={{ position: "absolute", top: 10, left: 10, background: "rgba(18,18,18,0.92)", border: "1px solid var(--pf-accent)", color: "var(--pf-accent)", padding: "6px 10px", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }} data-testid="model-focus-indicator">
          FOCUSED · {focusTag.tag}
        </div>
      )}

      {/* Bottom-left help */}
      <div className="pf-mono" style={{ position: "absolute", left: 10, bottom: 10, fontSize: 10, color: "var(--pf-text-muted)", background: "rgba(18,18,18,0.7)", padding: "5px 10px", border: "1px solid var(--pf-border)", letterSpacing: 1 }}>
        LEFT-DRAG ORBIT · RIGHT-DRAG PAN · WHEEL ZOOM · RIGHT-CLICK FOR PROPERTIES
      </div>

      {/* Context menu */}
      {menu && activeNode && (
        <div
          className="pf-card"
          style={{ position: "absolute", left: menu.x, top: menu.y, zIndex: 5, minWidth: 230, padding: 6, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", boxShadow: "0 8px 28px rgba(0,0,0,0.45)" }}
          onClick={(e) => e.stopPropagation()}
          data-testid="model-context-menu"
        >
          <div style={{ padding: "6px 10px", fontSize: 10, fontFamily: "JetBrains Mono", letterSpacing: 1.4, color: "var(--pf-text-muted)", textTransform: "uppercase" }}>
            {activeNode.kind || "node"} · {activeNode.tag || activeNode.id || prettifyKey(menu.nodeKey)}
          </div>
          <div className="pf-divider"/>
          <button className="btn btn-ghost" style={menuItemStyle()} onClick={() => { setPropsPanel({ nodeKey: menu.nodeKey, meta: activeNode }); setMenu(null); }} data-testid="model-context-show-properties">
            <Eye size={13}/> Show properties
          </button>
          <button className="btn btn-ghost" style={menuItemStyle()} onClick={() => { focusOnMeta(stateRef.current, activeNode); setMenu(null); }} data-testid="model-context-zoom">
            <Crosshair size={13}/> Zoom to {activeNode.kind === "pipe" ? "pipe" : "item"}
          </button>
        </div>
      )}

      {/* Properties drawer */}
      {propsPanel && (
        <div className="pf-card" style={drawerStyle()} data-testid="model-props-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>
            <div className="pf-overline" style={{ color: "var(--pf-accent)" }}>{propsPanel.meta.kind || "node"} · properties</div>
            <button className="btn btn-ghost" style={{ padding: 4, borderColor: "transparent" }} onClick={() => setPropsPanel(null)} data-testid="model-props-close"><X size={14}/></button>
          </div>
          <div style={{ padding: 14, overflowY: "auto" }}>
            <PropsTable meta={propsPanel.meta} />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------

function overlayBtnStyle() {
  return {
    background: "rgba(18,18,22,0.92)",
    backdropFilter: "blur(6px)",
    fontSize: 11,
    padding: "6px 10px",
  };
}

function menuItemStyle() {
  return {
    width: "100%",
    justifyContent: "flex-start",
    border: "1px solid transparent",
    fontSize: 12.5,
    padding: "8px 10px",
  };
}

function drawerStyle() {
  return {
    position: "absolute",
    right: 10,
    top: 56,
    bottom: 36,
    width: 320,
    zIndex: 4,
    background: "var(--pf-surface)",
    border: "1px solid var(--pf-border)",
    boxShadow: "0 8px 28px rgba(0,0,0,0.45)",
    display: "flex",
    flexDirection: "column",
  };
}

function prettifyKey(k) {
  if (!k) return "";
  const i = k.indexOf("__");
  if (i === -1) return k;
  return `${k.slice(0, i).toUpperCase()} · ${k.slice(i + 2)}`;
}

function PropsTable({ meta }) {
  const flat = useMemo(() => flatten(meta), [meta]);
  if (!flat.length) return <div style={{ color: "var(--pf-text-muted)", fontSize: 12 }}>No properties available for this node.</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <tbody>
        {flat.map(([k, v]) => (
          <tr key={k} style={{ borderBottom: "1px solid var(--pf-border-muted)" }}>
            <td style={{ padding: "6px 8px 6px 0", color: "var(--pf-text-muted)", fontFamily: "JetBrains Mono", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, verticalAlign: "top", width: "45%" }}>{k}</td>
            <td style={{ padding: "6px 0", color: "var(--pf-text)", wordBreak: "break-word" }}>{String(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function flatten(obj, prefix = "") {
  if (obj == null) return [];
  if (typeof obj !== "object") return [[prefix.replace(/^_+/, "") || "value", obj]];
  const rows = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || v === "") continue;
    const label = prefix ? `${prefix}.${k}` : k;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      rows.push([label, v.join(", ")]);
    } else if (typeof v === "object") {
      rows.push(...flatten(v, label));
    } else {
      rows.push([label, v]);
    }
  }
  return rows;
}

function frameAll(state) {
  const { root, camera, controls, renderer } = state;
  if (!root || !camera || !controls) return;
  // Compute bounds excluding the giant ground plane + pipe rack so the camera
  // zooms in on the actual equipment.
  const box = new THREE.Box3();
  let any = false;
  root.traverse((obj) => {
    if (!obj.isMesh) return;
    // Walk up to find the named ancestor; skip ground / rack
    let n = obj;
    while (n && !(n.name && n.name.length)) n = n.parent;
    const name = n && n.name ? n.name : "";
    if (name === "ground" || name === "rack" || name.startsWith("rack")) return;
    obj.updateWorldMatrix(true, false);
    const child = new THREE.Box3().setFromObject(obj);
    if (isFinite(child.min.x)) {
      box.union(child);
      any = true;
    }
  });
  // Fallback to whole scene bounds if no equipment nodes were found
  if (!any) box.setFromObject(root);
  if (!isFinite(box.min.x)) return;
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const dist = Math.abs(maxDim / (2 * Math.tan(fov / 2))) * 1.5;
  // Pick a pleasant 3/4 perspective angle
  const dirn = new THREE.Vector3(1, -1, 0.6).normalize();
  camera.position.copy(center.clone().add(dirn.multiplyScalar(dist)));
  controls.target.copy(center);
  camera.near = Math.max(dist / 200, 0.05);
  camera.far = dist * 8;
  camera.updateProjectionMatrix();
  controls.update();
  if (renderer) renderer.render(state.scene, camera);
}

function flyTo(state, targetVec, distance) {
  const { camera, controls } = state;
  if (!camera || !controls) return;
  controls.target.copy(targetVec);
  const dirn = camera.position.clone().sub(targetVec).normalize();
  if (dirn.lengthSq() < 1e-6) dirn.set(1, -1, 0.6).normalize();
  camera.position.copy(targetVec.clone().add(dirn.multiplyScalar(distance)));
  controls.update();
}

function focusOnMeta(state, nodeMeta) {
  if (!nodeMeta) return;
  const pos = nodeMeta.position || nodeMeta.center;
  if (pos && typeof pos.x === "number") {
    flyTo(state, new THREE.Vector3(pos.x, pos.y, pos.z), 14);
  } else {
    frameAll(state);
  }
}

function applyHover(state, node) {
  state.hoveredObject = node;
  // Walk meshes inside this node and tint their emissive
  const stash = [];
  node.traverse((obj) => {
    if (obj.isMesh && obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if ("emissive" in m) {
          stash.push([m, m.emissive.clone(), m.emissiveIntensity ?? 1]);
          m.emissive = new THREE.Color("#F26B1F"); // Spark Orange highlight
          m.emissiveIntensity = 0.55;
        }
      });
    }
  });
  state.hoveredOriginalEmissive = stash;
}

function restoreHover(state) {
  if (!state.hoveredOriginalEmissive) return;
  state.hoveredOriginalEmissive.forEach(([m, color, intensity]) => {
    m.emissive = color;
    m.emissiveIntensity = intensity;
  });
  state.hoveredOriginalEmissive = null;
  state.hoveredObject = null;
}
